import React, { useState } from "react";
import {
  Badge,
  Box,
  Button,
  Card,
  Cell,
  FormField,
  Input,
  InputArea,
  Layout,
  Page,
  SectionHelper,
  Text,
  WixDesignSystemProvider,
} from "@wix/design-system";
import "@wix/design-system/styles.global.css";
import Axios from "axios";
import { showToast } from "@wix/dashboard-sdk";
import { withProviders } from "../../withProviders";
import FaireConfig from "../../../config/faire";
import Instance from "../../../utilities/instance";
import styles from "./page.module.css";
import Site from "../../../models/site";
import TrialExpiryAlert from "../../../components/trial-expiry-alert";
import Loading from "../../../components/common/Loader";
import { authAxios } from "../../../services/axios/default";
type ErrorMessage = {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
};

const Index = () => {
  const [storeData, setStoreData] = useState({
    active_products: null,
    store_name: null,
  });

  /*
    |--------------------------------------------------------------------------
    | Set Form Inputs States
    |--------------------------------------------------------------------------
    |
    */

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  // Loading State
  const [isLoading, setIsLoading] = useState(false);

  /*
    |--------------------------------------------------------------------------
    | Form Change Handlers
    |--------------------------------------------------------------------------
    |
    */

  const changeNameHandler = (event: any) => setName(event.target.value);
  const changeEmailHandler = (event: any) => setEmail(event.target.value);
  const changeSubjectHandler = (event: any) => setSubject(event.target.value);
  const changeMessageHandler = (event: any) => setMessage(event.target.value);

  /*
    |--------------------------------------------------------------------------
    | Validation Errors
    |--------------------------------------------------------------------------
    |
    */

  const [validationError, setValidationError] = useState<ErrorMessage>({});

  /*  
    |--------------------------------------------------------------------------
    | Insert Form Data
    |--------------------------------------------------------------------------
    |
    */

  const sendMessage = () => {
    setIsLoading(true);

    /*
    |--------------------------------------------------------------------------
    | Contact Data
    |--------------------------------------------------------------------------
    |
    */
    let data = {
      name: name,
      email: email,
      subject: subject,
      message: message,
    };

    /*
    |--------------------------------------------------------------------------
    | Post Data
    |--------------------------------------------------------------------------
    |
    */
    authAxios.post(FaireConfig.backendApiBaseUrl + "/api/contact/send", data, {
      headers: {
        Authorization: Instance.get(),
      },
    })
      .then((response) => {
        setValidationError({});

        // Reset Form
        setEmail("");
        setName("");
        setMessage("");
        setSubject("");

        showToast({
          type: "success",
          message: "Your Message Has Been Sent Successfully",
        });
      })
      .catch((error) => {
        let { email, name, subject, message } = error.response.data.errors ?? {};

        showToast({
          type: "error",
          message: "Failed To send Message",
        });

        setValidationError({
          name: name || "",
          email: email || "",
          subject: subject || "",
          message: message || "",
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  /*
    |--------------------------------------------------------------------------
    | Submit Contact Form
    |--------------------------------------------------------------------------
    |
    */
  const submitHandler = (event: any) => {
    event.preventDefault();
    setValidationError({});

    // Send Message
    sendMessage();
  };

  React.useEffect(() => {
    Site.faireStoreDetails()
      .then((response: any) => setStoreData(response.data))
      .catch((error) => console.error(error));
  }, []);

  return (
    <WixDesignSystemProvider>
      <Page height="95vh">
        <Page.Header
          title="Contact"
          subtitle="Have questions or concerns? Fill out the form below, and our support team will get back to you."
        />
        <Page.Content>
          <TrialExpiryAlert />
          <Box marginBottom="20px">
            <SectionHelper appearance="danger" fullWidth>
              <Text weight="bold" size="small">
                We are in the process of removing the Contact Us page. To reach us, please use the Contact Us chat box.
              </Text>
            </SectionHelper>
          </Box>
          <Layout gap="30px" cols={12}>
            <Cell span={8}>
              <Card>
                <Card.Header title="Let's Have a Chat" />
                <Card.Divider />

                <Card.Content>
                  <Cell className={styles.spacing}>
                    <FormField
                      status={validationError.name ? "error" : ""}
                      statusMessage={validationError.name}
                      label="Your Name *"
                    >
                      <Input disabled size="large" value={name || ""} onChange={changeNameHandler} />
                    </FormField>
                  </Cell>

                  <Cell className={styles.spacing}>
                    <FormField
                      label="Email*"
                      status={validationError.email ? "error" : ""}
                      statusMessage={validationError.email}
                    >
                      <Input disabled size="large" onChange={changeEmailHandler} value={email || ""} />
                    </FormField>
                  </Cell>

                  <Cell className={styles.spacing}>
                    <FormField
                      label="Subject*"
                      status={validationError.subject ? "error" : ""}
                      statusMessage={validationError.subject}
                    >
                      <Input disabled size="large" onChange={changeSubjectHandler} value={subject || ""} />
                    </FormField>
                  </Cell>

                  <Cell className={styles.spacing}>
                    <FormField
                      label="Your Message*"
                      status={validationError.message ? "error" : ""}
                      statusMessage={validationError.message}
                    >
                      <InputArea
                        rows={5}
                        disabled
                        size="medium"
                        resizable
                        onChange={changeMessageHandler}
                        value={message || ""}
                      />
                    </FormField>
                  </Cell>

                  <Cell className={styles.spacing}>
                    <FormField>
                      <Button disabled onClick={submitHandler}>
                        {isLoading ? (
                          <>
                            <Loading size="tiny" /> Sending
                          </>
                        ) : (
                          `Send`
                        )}
                      </Button>
                    </FormField>
                  </Cell>
                </Card.Content>
              </Card>
            </Cell>
            <Cell span={4}>
              <Card>
                <Card.Header title="Faire Store Details" />
                <Card.Divider />
                <Card.Content>
                  <Box height="130px" direction="vertical" gap="18px">
                    <Box align="space-between">
                      <Text size="small"> Store Name </Text>
                      <Text skin="primary" weight="bold" size="small">
                        {storeData?.store_name}
                      </Text>
                    </Box>
                    <Card.Divider />

                    <Box align="space-between">
                      <Text size="small"> Status </Text>
                      <Badge skin="success" size="small">
                        Live
                      </Badge>
                    </Box>
                    <Card.Divider />

                    <Box align="space-between">
                      <Text size="small"> Active Products </Text>
                      <Text skin="primary" weight="bold" size="small">
                        {storeData?.active_products}
                      </Text>
                    </Box>
                  </Box>
                </Card.Content>
              </Card>
            </Cell>
          </Layout>
        </Page.Content>
      </Page>
    </WixDesignSystemProvider>
  );
};

export default withProviders(Index);
